import { Link } from "react-router-dom";
import { openCookieSettings } from "@/components/cookies/consent";
import { Phone, MapPin, Linkedin, Instagram, ArrowUpRight, ArrowRight } from "lucide-react";
import logo from "@/assets/FounderMitra_logo_trimmed.webp";
import { openWhatsApp, WHATSAPP_URL } from "@/lib/whatsapp";

// WhatsApp SVG icon component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const navigation = {
  services: [
    { name: "Strategic Technology Advisory", href: "/services/technology-advisory" },
    { name: "AI Advisory", href: "/services/ai-advisory" },
    { name: "AI Enablement", href: "/services/ai-enablement" },
    { name: "Investor Tech Readiness", href: "/services/investor-tech-readiness" },
    { name: "Investor Readiness", href: "/services/investor-readiness" },
    { name: "Product Discovery & MVP", href: "/services/product-discovery-mvp" },
    { name: "Executive Dashboards", href: "/services/executive-dashboards" },
    { name: "Prototyping", href: "/services/prototyping" },
    { name: "Discovery Sessions", href: "/services/discovery-sessions" },
  ],
  company: [
    { name: "About", href: "/about" },
    { name: "How We Think", href: "/how-we-think" },
    { name: "How We Work", href: "/how-we-work" },
    { name: "Strategic Engagements", href: "/strategic-engagements" },
    { name: "Industries", href: "/industries" },
    { name: "Testimonials", href: "/testimonials" },
    { name: "Insights", href: "/insights" },
  ],
  support: [
    { name: "Contact Us", href: "/contact" },
    { name: "Book a Call", href: "/book-call" },
    { name: "AI & Tech Readiness Audit", href: "/ai-tech-readiness-audit" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
  ],
};

export function Footer() {
  return (
   <footer id="site-footer" className="relative isolate overflow-hidden bg-[#0a0a0f] text-white">
      {/* Brand glow wash */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none opacity-80"
        style={{
          background:
            "radial-gradient(60% 50% at 12% 8%, hsl(248 70% 30% / 0.35), transparent 65%), radial-gradient(45% 50% at 90% 90%, hsl(40 52% 58% / 0.18), transparent 65%)",
        }}
      />
      {/* Subtle grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />

      <div className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-7xl pt-20 md:pt-28 pb-10">
        {/* Editorial top: oversized statement */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16 md:pb-20 border-b border-white/10">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-accent mb-6">
              Founder Mitra
            </p>
            <h2 className="font-display font-bold text-white tracking-[-0.025em] leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-2xl">
              A senior technology &amp; AI advisory partner for the founder journey.
            </h2>
            <p className="mt-6 text-white/65 max-w-xl leading-[1.75] text-base md:text-lg">
              FounderMitra is a founder-first strategic technology and AI advisory partner helping leadership teams make sharper decisions across product, architecture, AI and execution.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/book-call"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[hsl(248_62%_21%)] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="grid place-items-center h-9 w-9 rounded-full bg-[hsl(248_62%_21%)] text-white transition-transform duration-300 group-hover:translate-x-0.5">
                  <ArrowRight className="h-4 w-4" />
                </span>
                Book a Discovery Call
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] backdrop-blur-md px-6 py-3 text-sm font-medium text-white hover:bg-white/[0.12] transition-all duration-300"
              >
                Contact Us
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 flex lg:justify-end">
            <Link to="/" className="inline-block group">
              <img
                src={logo}
                alt="Founder Mitra"
                width={240}
                height={64}
                loading="lazy"
                decoding="async"
                className="h-12 md:h-14 lg:h-16 w-auto brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
              />
            </Link>
          </div>
        </div>

        {/* Link grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-12 py-14">
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent mb-5">Services</h3>
            <ul className="space-y-3">
              {navigation.services.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-sm text-white/65 hover:text-white transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent mb-5">Company</h3>
            <ul className="space-y-3">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-sm text-white/65 hover:text-white transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent mb-5">Support</h3>
            <ul className="space-y-3">
              {navigation.support.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-sm text-white/65 hover:text-white transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent mb-5">Reach Us</h3>
            <ul className="space-y-3.5 text-sm text-white/65">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 flex-shrink-0 text-accent mt-0.5" />
                <span>Kolkata, India</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 text-accent mt-0.5" />
                <a href="tel:+918293273236" className="hover:text-white transition-colors">+91-8293273236</a>
              </li>
              <li className="flex items-start gap-3">
                <WhatsAppIcon className="h-4 w-4 flex-shrink-0 text-accent mt-0.5" />
                <a href={WHATSAPP_URL} onClick={openWhatsApp} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp Us</a>
              </li>
            </ul>

            <div className="mt-6 flex gap-3">
              <a
                href="https://www.linkedin.com/company/founder-mitra/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.05] backdrop-blur-md flex items-center justify-center text-white/75 hover:text-white hover:bg-white/15 transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <a
                href="https://www.instagram.com/foundermitra_real?igsh=bWRpb2VqMjRjZmNo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.05] backdrop-blur-md flex items-center justify-center text-white/75 hover:text-white hover:bg-white/15 transition-all"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-center justify-between">
          <p className="text-xs text-white/45">
            © {new Date().getFullYear()} Founder Mitra. All rights reserved.
          </p>
          <div className="flex gap-6 text-xs text-white/45">
            <Link to="/privacy" className="hover:text-white/80 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/80 transition-colors">Terms</Link>
            <button
              type="button"
              onClick={openCookieSettings}
              className="hover:text-white/80 transition-colors"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
